import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { DonationCampaign, subscribeDonationCampaign, subscribeDonationCampaignByDashboardSlug } from "../data/donations";

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function campaignPercent(campaign: DonationCampaign | null) {
  if (!campaign?.goalAmount) return 0;
  return Math.max(0, Math.min(100, Math.round((campaign.raisedAmount / campaign.goalAmount) * 100)));
}

function CampaignDashboardDisplay({ campaign, title }: { campaign: DonationCampaign; title?: string }) {
  const percent = campaignPercent(campaign);
  const remaining = Math.max(0, campaign.goalAmount - campaign.raisedAmount);
  const displayTitle = title || campaign.liveDashboardTitle || campaign.title;

  return (
    <div className="live-campaign-dashboard" style={{ "--live-progress": `${percent}%` } as CSSProperties}>
      <div className="live-campaign-header">
        <p className="eyebrow">Live Campaign Progress</p>
        <h2>{displayTitle}</h2>
      </div>
      <div className="live-campaign-stage">
        <div className="live-campaign-gauge" aria-label={`${displayTitle} progress`}>
          <div className="live-campaign-gauge-fill" />
          <div className="live-campaign-gauge-center">
            <strong>{percent}%</strong>
            <span>to goal</span>
          </div>
        </div>
        <div className="live-campaign-total">
          <span>Raised so far</span>
          <strong>{formatCurrency(campaign.raisedAmount)}</strong>
          <small>{formatCurrency(campaign.goalAmount)} goal</small>
        </div>
      </div>
      <div className="live-campaign-meter" aria-hidden="true">
        <span />
      </div>
      <div className="live-campaign-stats">
        <div>
          <span>Remaining</span>
          <strong>{formatCurrency(remaining)}</strong>
        </div>
        <div>
          <span>Goal</span>
          <strong>{formatCurrency(campaign.goalAmount)}</strong>
        </div>
      </div>
    </div>
  );
}

export function CampaignDashboard({
  campaignId,
  dashboardSlug,
  title,
  preview = false,
}: {
  campaignId?: string;
  dashboardSlug?: string;
  title?: string;
  preview?: boolean;
}) {
  const [campaign, setCampaign] = useState<DonationCampaign | null>(null);
  const [loaded, setLoaded] = useState(false);

  const previewCampaign = useMemo<DonationCampaign>(() => ({
    id: "preview",
    title: title || "Campaign Dashboard",
    summary: "",
    goalAmount: 100000,
    raisedAmount: 64000,
    isPublished: true,
    showOnHome: false,
    sortOrder: 0,
    status: "active",
  }), [title]);

  useEffect(() => {
    if (preview) return;
    setLoaded(false);
    const campaignIdValue = String(campaignId || "");
    const dashboardSlugValue = String(dashboardSlug || "");
    const subscribe = campaignIdValue ? subscribeDonationCampaign : subscribeDonationCampaignByDashboardSlug;
    const unsubscribe = subscribe(
      campaignIdValue || dashboardSlugValue,
      (next) => {
        setCampaign(next);
        setLoaded(true);
      },
      () => setLoaded(true),
    );
    return unsubscribe;
  }, [campaignId, dashboardSlug, preview]);

  if (preview) return <CampaignDashboardDisplay campaign={previewCampaign} title={title} />;
  if (!campaignId && !dashboardSlug) return <div className="live-campaign-placeholder">Campaign progress is not connected yet.</div>;
  if (!loaded) return <div className="live-campaign-placeholder">Loading campaign progress...</div>;
  if (!campaign) return <div className="live-campaign-placeholder">Campaign progress is not available yet.</div>;

  return <CampaignDashboardDisplay campaign={campaign} title={title} />;
}
